<script  module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { onDestroy } from "svelte";
  import { router, usePage } from "@inertiajs/svelte";
  import { ArrowLeft, Loader2, Save, Layers, Bold, Italic, List, ListOrdered, Heading1, Heading2, Quote, Code, Image as ImageIcon, Video } from "lucide-svelte";
  import { toast } from "svelte-sonner";
  import { Button } from "$lib/components/ui/button/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import { Label } from "$lib/components/ui/label/index.js";
  import { Textarea } from "$lib/components/ui/textarea/index.js";
  import { Checkbox } from "$lib/components/ui/checkbox/index.js";
  import * as Select from "$lib/components/ui/select/index.js";
  import { appStore } from '$lib/stores/appStore';
  import { Editor } from "@tiptap/core";
  import StarterKit from "@tiptap/starter-kit";
  import Image from "@tiptap/extension-image";
  import Youtube from "@tiptap/extension-youtube";

  let { post = {}, category_options = [] } = $props();

  let slug = $state(post.slug || "");
  let category_id = $state(post.category_id || "");
  let cover_url = $state(post.cover_url || "");
  let published = $state(!!post.published);
  let published_at = $state(post.published_at || "");
  let isSubmitting = $state(false);

  const page = usePage();
  const translationsData = $derived((page.props as any).translations || {});
  const t = $derived(translationsData.posts || {});

  let translations = $state(JSON.parse(JSON.stringify(post.translations_attributes || [])));
  let activeLocaleTab = $state(translations[0]?.locale || "zh");

  const isCreatePage = $derived(typeof window !== 'undefined' && (window.location.pathname.endsWith('/new') || window.location.pathname.includes('/posts/new')));

  const selectOptions = $derived([
    { value: "", label: t.uncategorized || "未分类" },
    ...category_options.map((opt: any) => ({ value: String(opt.value), label: opt.label }))
  ]);

  let currentSelectTriggerText = $derived(
    selectOptions.find(opt => opt.value === String(category_id))?.label || t.select_category_hint || "选择所属分类"
  );

  let editorInstances = $state<Record<string, Editor>>({});
  let editorContainers: Record<string, HTMLDivElement> = {};
  let updateCounter = $state(0);

  function initEditor(locale: string, contentData: any) {
    if (editorInstances[locale] || !editorContainers[locale]) return;

    const initialContent = typeof contentData === 'string' 
      ? contentData 
      : (Array.isArray(contentData) ? "" : JSON.stringify(contentData || ""));

    const editor = new Editor({
      element: editorContainers[locale],
      extensions: [
        StarterKit,
        Image.configure({
          HTMLAttributes: {
            class: 'rounded-xl max-w-full my-6 mx-auto shadow-sm border border-zinc-100 dark:border-zinc-800',
          },
        }),
        Youtube.configure({
          HTMLAttributes: {
            class: 'rounded-xl aspect-video w-full my-6 shadow-sm border border-zinc-100 dark:border-zinc-800',
          },
          addPasteHandler: true,
        }),
      ],
      content: initialContent,
      editorProps: {
        attributes: {
          class: "prose prose-zinc dark:prose-invert focus:outline-none min-h-[320px] max-h-[600px] overflow-y-auto px-4 py-3 text-sm text-zinc-800 dark:text-zinc-200 leading-relaxed",
        },
      },
      onUpdate: ({ editor }) => {
        const currentTranslation = translations.find((tr: any) => tr.locale === locale);
        if (currentTranslation) {
          currentTranslation.content_blocks = editor.getHTML();
        }
      },
      onSelectionUpdate: () => { updateCounter++; },
      onTransaction: () => { updateCounter++; }
    });

    editorInstances[locale] = editor;
  }

  function addImage(locale: string) {
    const url = prompt(t.prompt_image || 'Enter image URL:');
    if (url && editorInstances[locale]) {
      editorInstances[locale].chain().focus().setImage({ src: url }).run();
    }
  }

  function addVideo(locale: string) {
    const url = prompt(t.prompt_video || 'Enter video URL:');
    if (url && editorInstances[locale]) {
      editorInstances[locale].chain().focus().setYoutubeVideo({ src: url }).run();
    }
  }


    
  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);

    if (activeLocaleTab && editorContainers[activeLocaleTab]) {
      const currentTrans = translations.find((tr: any) => tr.locale === activeLocaleTab);
      if (currentTrans && !editorInstances[activeLocaleTab]) {
        setTimeout(() => {
          initEditor(activeLocaleTab, currentTrans.content_blocks);
        }, 1);
      }
    }
  });

  onDestroy(() => {
    Object.values(editorInstances).forEach(editor => {
      if (editor) editor.destroy();
    });
  });

  function savePost(e: Event) {
    e.preventDefault();
    if (isSubmitting) return;
    if (!slug) { toast.error(t.error_slug_required || "Slug required"); return; }

    isSubmitting = true;
    const toastId = toast.loading(t.toast_saving || "Saving...");

    const payload = {
      post: {
        slug,
        category_id: category_id === "" ? null : category_id,
        cover_url,
        published,
        published_at: published_at === "" ? null : published_at,
        translations_attributes: translations.map((tr: any) => ({
          id: isCreatePage ? null : (tr.id || null),
          locale: tr.locale,
          title: tr.title,
          excerpt: tr.excerpt,
          content_blocks: tr.content_blocks
        }))
      }
    };

    const config = {
      onSuccess: () => toast.success(t.toast_save_success || "Success", { id: toastId }),
      onError: () => { toast.error(t.toast_save_error || "Error", { id: toastId }); isSubmitting = false; },
      onFinish: () => isSubmitting = false
    };

    if (post.id && !isCreatePage) {
      router.put(`/posts/${post.id}`, payload, config);
    } else {
      router.post("/posts", payload, config);
    }
  }
</script>

<div class="p-8 max-w-6xl mx-auto">
  <Button variant="ghost" size="sm" onclick={() => router.get('/posts')} class="text-xs text-muted-foreground hover:text-foreground mb-6 -ml-3 gap-1.5">
    <ArrowLeft class="size-3.5" /> {t.back_to_list}
  </Button>

  <div class="mb-6">
    <h1 class="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
      {post.id && !isCreatePage ? t.edit_title : t.new_title}
    </h1>
  </div>

  <form onsubmit={savePost} class="grid grid-cols-1 lg:grid-cols-3 gap-8">
    <div class="lg:col-span-1 space-y-6">
      <div class="bg-white dark:bg-zinc-900 border dark:border-zinc-800 p-6 rounded-xl space-y-5 shadow-sm">
        <h3 class="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b dark:border-zinc-800 pb-2">{t.sidebar_title}</h3>
        
        <div class="space-y-2">
          <Label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t.field_category}</Label>
          <Select.Root type="single" value={String(category_id)} onValueChange={(val) => category_id = val}>
            <Select.Trigger class="w-full rounded-lg border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 focus:ring-zinc-950 text-zinc-900 dark:text-zinc-100">
              {currentSelectTriggerText}
            </Select.Trigger>
            <Select.Content class="rounded-lg shadow-md border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              {#each selectOptions as opt}
                <Select.Item value={opt.value} class="text-sm cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-900 dark:text-zinc-100">
                  {opt.label}
                </Select.Item>
              {/each}
            </Select.Content>
          </Select.Root>
        </div>

        <div class="space-y-2">
          <Label for="slug" class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t.field_slug}</Label>
          <Input id="slug" type="text" bind:value={slug} required placeholder="example-post-slug" class="rounded-lg border-zinc-200 dark:border-zinc-800 focus-visible:ring-zinc-950 dark:bg-zinc-950" />
        </div>

        <div class="space-y-2">
          <Label for="cover_url" class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t.field_cover}</Label>
          <Input id="cover_url" type="text" bind:value={cover_url} placeholder="https://img.domain.com/cover.jpg" class="rounded-lg border-zinc-200 dark:border-zinc-800 focus-visible:ring-zinc-950 dark:bg-zinc-950" />
        </div>

        <div class="pt-4 border-t dark:border-zinc-800 space-y-4">
          <div class="flex items-center space-x-2">
            <Checkbox id="published" bind:checked={published} class="rounded-md border-zinc-300 dark:border-zinc-700 focus-visible:ring-zinc-950" />
            <label for="published" class="text-xs font-medium cursor-pointer select-none text-zinc-700 dark:text-zinc-300">{t.field_publish_now}</label>
          </div>

          <div class="space-y-2">
            <Label for="published_at" class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t.field_publish_at}</Label>
            <Input id="published_at" type="datetime-local" bind:value={published_at} class="rounded-lg border-zinc-200 dark:border-zinc-800 focus-visible:ring-zinc-950 dark:bg-zinc-950" />
          </div>
        </div>

        <Button type="submit" disabled={isSubmitting} class="w-full bg-zinc-900 dark:bg-zinc-100 hover:opacity-90 text-white dark:text-zinc-900 font-medium rounded-lg text-sm h-10 shadow flex items-center justify-center gap-2">
          {#if isSubmitting}
            <Loader2 class="size-4 animate-spin" /> {t.toast_saving}
          {:else}
            <Save class="size-4" /> {t.btn_save}
          {/if}
        </Button>
      </div>
    </div>

    <div class="lg:col-span-2 bg-white dark:bg-zinc-900 border dark:border-zinc-800 p-6 rounded-xl shadow-sm space-y-6">
      <div class="flex justify-between items-center border-b dark:border-zinc-800 pb-4">
        <h2 class="text-sm font-semibold text-zinc-950 dark:text-zinc-50 flex items-center gap-1.5">
          <Layers class="size-4 text-zinc-700 dark:text-zinc-300" /> {t.content_editor_title}
        </h2>

        <div class="flex gap-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-lg">
          {#each translations as tr}
            <button 
              type="button" 
              onclick={() => activeLocaleTab = tr.locale}
              class="px-3 py-1 rounded-md text-xs font-medium uppercase transition-all {activeLocaleTab === tr.locale ? 'bg-white dark:bg-zinc-950 text-zinc-950 dark:text-zinc-50 shadow-sm' : 'text-zinc-400 hover:text-zinc-600 dark:text-zinc-500'}"
            >
              {tr.locale}
            </button>
          {/each}
        </div>
      </div>

      {#each translations as tr (tr.locale)}
        <div class={activeLocaleTab === tr.locale ? "space-y-5 animate-in fade-in duration-200" : "hidden"}>
          
          <div class="space-y-2">
            <Label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t.field_title} [{tr.locale.toUpperCase()}]</Label>
            <Input type="text" bind:value={tr.title} class="rounded-lg border-zinc-200 dark:border-zinc-800 focus-visible:ring-zinc-950 font-medium text-base text-zinc-900 dark:text-zinc-100 dark:bg-zinc-950" placeholder={t.placeholder_title} />
          </div>

          <div class="space-y-2">
            <Label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t.field_excerpt}</Label>
            <Textarea bind:value={tr.excerpt} rows={2} class="rounded-lg border-zinc-200 dark:border-zinc-800 focus-visible:ring-zinc-950 resize-none text-zinc-600 dark:text-zinc-300 dark:bg-zinc-950" placeholder={t.placeholder_excerpt} />
          </div>

          <div class="space-y-2">
            <Label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t.field_content}</Label>
            
            <div class="rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden shadow-sm bg-white dark:bg-zinc-950 focus-within:ring-1 focus-within:ring-zinc-950 dark:focus-within:ring-zinc-300 focus-within:border-zinc-950 dark:focus-within:border-zinc-300">
              {#if editorInstances[tr.locale]}
                <div class="flex flex-wrap items-center gap-0.5 bg-zinc-50/80 dark:bg-zinc-900/80 border-b dark:border-zinc-800 p-1.5 text-zinc-600 dark:text-zinc-400">
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 {editorInstances[tr.locale].isActive('bold') && updateCounter ? 'bg-zinc-200 dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50' : ''}" onclick={() => editorInstances[tr.locale].chain().focus().toggleBold().run()}>
                    <Bold class="size-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 {editorInstances[tr.locale].isActive('italic') && updateCounter ? 'bg-zinc-200 dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50' : ''}" onclick={() => editorInstances[tr.locale].chain().focus().toggleItalic().run()}>
                    <Italic class="size-3.5" />
                  </Button>
                  <div class="h-4 w-[1px] bg-zinc-200 dark:bg-zinc-800 mx-1"></div>
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 {editorInstances[tr.locale].isActive('heading', { level: 1 }) && updateCounter ? 'bg-zinc-200 dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50' : ''}" onclick={() => editorInstances[tr.locale].chain().focus().toggleHeading({ level: 1 }).run()}>
                    <Heading1 class="size-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 {editorInstances[tr.locale].isActive('heading', { level: 2 }) && updateCounter ? 'bg-zinc-200 dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50' : ''}" onclick={() => editorInstances[tr.locale].chain().focus().toggleHeading({ level: 2 }).run()}>
                    <Heading2 class="size-3.5" />
                  </Button>
                  <div class="h-4 w-[1px] bg-zinc-200 dark:bg-zinc-800 mx-1"></div>
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 {editorInstances[tr.locale].isActive('bulletList') && updateCounter ? 'bg-zinc-200 dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50' : ''}" onclick={() => editorInstances[tr.locale].chain().focus().toggleBulletList().run()}>
                    <List class="size-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 {editorInstances[tr.locale].isActive('orderedList') && updateCounter ? 'bg-zinc-200 dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50' : ''}" onclick={() => editorInstances[tr.locale].chain().focus().toggleOrderedList().run()}>
                    <ListOrdered class="size-3.5" />
                  </Button>
                  <div class="h-4 w-[1px] bg-zinc-200 dark:bg-zinc-800 mx-1"></div>
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 {editorInstances[tr.locale].isActive('blockquote') && updateCounter ? 'bg-zinc-200 dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50' : ''}" onclick={() => editorInstances[tr.locale].chain().focus().toggleBlockquote().run()}>
                    <Quote class="size-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 {editorInstances[tr.locale].isActive('codeBlock') && updateCounter ? 'bg-zinc-200 dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50' : ''}" onclick={() => editorInstances[tr.locale].chain().focus().toggleCodeBlock().run()}>
                    <Code class="size-3.5" />
                  </Button>
                  
                  <div class="h-4 w-[1px] bg-zinc-200 dark:bg-zinc-800 mx-1"></div>
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 text-zinc-600 dark:text-zinc-400 hover:text-zinc-950 dark:hover:text-zinc-50" onclick={() => addImage(tr.locale)} title={t.tooltip_image}>
                    <ImageIcon class="size-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" type="button" class="size-7 rounded p-0 text-zinc-600 dark:text-zinc-400 hover:text-zinc-950 dark:hover:text-zinc-50" onclick={() => addVideo(tr.locale)} title={t.tooltip_video}>
                    <Video class="size-3.5" />
                  </Button>
                </div>
              {/if}

              <div bind:this={editorContainers[tr.locale]}></div>
            </div>

          </div>
        </div>
      {/each}
    </div>
  </form>
</div>

<style global>
  .prose ul { list-style-type: disc; padding-left: 1.25rem; margin-bottom: 0.5rem; }
  .prose ol { list-style-type: decimal; padding-left: 1.25rem; margin-bottom: 0.5rem; }
  .prose h1 { font-size: 1.5rem; font-weight: 700; margin-top: 1rem; margin-bottom: 0.5rem; }
  .prose h2 { font-size: 1.25rem; font-weight: 600; margin-top: 0.75rem; margin-bottom: 0.5rem; }
  .prose blockquote { border-left: 3px solid #e4e4e7; padding-left: 1rem; color: #71717a; font-style: italic; margin: 0.5rem 0; }
  .prose code { background-color: #f4f4f5; padding: 0.125rem 0.25rem; font-size: 0.85em; border-radius: 0.25rem; font-family: monospace; }
  .prose pre { background-color: #18181b; color: #f4f4f5; padding: 0.75rem; border-radius: 0.5rem; font-family: monospace; font-size: 0.85em; overflow-x: auto; margin: 0.5rem 0; }
  
  /* 暗黑模式下 Tiptap 样式的简单补充修正 */
  .dark .prose h1, .dark .prose h2 { color: #f4f4f5; }
  .dark .prose blockquote { border-left-color: #3f3f46; color: #a1a1aa; }
  .dark .prose code { background-color: #27272a; color: #f4f4f5; }
</style>